import { useEffect, useState, useMemo, useRef } from 'react'
import { Modal } from 'components'
import ErrorMessage from 'components/forms/error-message'
import { lang } from 'config'
import _ from 'lodash'
import { Plus } from 'components/icons/outline'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import nProgress from 'nprogress'
import Label from 'components/forms/label'
import InputDate from 'components/forms/input-date'
import moment from 'moment'
import InputAmount from 'components/forms/input-amount'
import { usePage } from 'contexts/page-context'
import Description from 'components/forms/description'
import Input from 'components/forms/input'
import useProductTypeRepositories from 'repositories/product-type'
import useVariant from 'repositories/variant'

const Content = ({ tags, onSubmit }) => {
  const { configuration } = usePage()

  /* ================= helper ================= */
  const toSlug = (s) => {
    const t = String(s || '').trim()
    return t ? _.kebabCase(t) : null
  }

  // Normalisasi existing tags -> Set slug per sumber
  const { disabledPtSet, disabledVsSet } = useMemo(() => {
    const pt = new Set()
    const vs = new Set()
    const list = Array.isArray(tags?.data) ? tags.data : (Array.isArray(tags) ? tags : [])
    for (const itRaw of list) {
      let slug = null
      let source = null
      if (typeof itRaw === 'string') {
        slug = itRaw
      } else if (itRaw && typeof itRaw === 'object') {
        slug = itRaw.slug ?? itRaw?.tag?.slug ?? itRaw.code ?? itRaw.key ?? toSlug(itRaw.name ?? itRaw?.tag?.name)
        source = itRaw.source ?? itRaw.type ?? itRaw.category ?? null
      }
      slug = slug ? String(slug).trim() : null
      if (!slug) continue
      if (source === 'product_type') { pt.add(slug) }
      else if (source === 'variant') { vs.add(slug) }
      else { // kalau tidak jelas sumbernya, disable di keduanya agar tidak dobel
        pt.add(slug); vs.add(slug)
      }
    }
    return { disabledPtSet: pt, disabledVsSet: vs }
  }, [tags])

  /* ================= sumber data ================= */
  const { data: productTypeSelection, isLoading: isLoadingProductType } = useProductTypeRepositories({
    paginate: false,
    order_by: "product_type.name",
    direction: "asc"
  })

  const { data: variantSelection, isLoading: isLoadingVariant } = useVariant({
    paginate: false,
    order_by: "variants.name",
    direction: "asc"
  })

  const [errors, setErrors] = useState({})

  /* ================= Multiselect Product Type ================= */
  const [ptOpen, setPtOpen] = useState(false)
  const [ptKeyword, setPtKeyword] = useState('')
  const [selectedProductTypeSlugs, setSelectedProductTypeSlugs] = useState([])
  const ptRef = useRef(null)

  /* ================= Multiselect Variant ====================== */
  const [vsOpen, setVsOpen] = useState(false)
  const [vsKeyword, setVsKeyword] = useState('')
  const [selectedVariantSlugs, setSelectedVariantSlugs] = useState([])
  const vsRef = useRef(null)

  /* =============== close on outside click / ESC untuk keduanya =============== */
  useEffect(() => {
    const onClickOutside = (e) => {
      if (ptRef.current && !ptRef.current.contains(e.target)) setPtOpen(false)
      if (vsRef.current && !vsRef.current.contains(e.target)) setVsOpen(false)
    }
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setPtOpen(false)
        setVsOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEsc)
    }
  }, [])

  /* ===== Source builder: bikin options + index map(slug -> full row dgn slug/name terjamin) ===== */
  const buildSource = (rawList, pickName, pickSlug) => {
    const list = Array.isArray(rawList?.data) ? rawList.data : (Array.isArray(rawList) ? rawList : [])
    const index = new Map()
    const options = []
    for (const it of list) {
      const name = pickName(it)
      const slug = pickSlug(it, name)
      if (!name || !slug) continue
      const key = String(slug)
      if (index.has(key)) continue
      const fullRow = { ...it }
      if (fullRow.slug == null) fullRow.slug = key
      if (fullRow.name == null) fullRow.name = String(name)
      index.set(key, fullRow)
      options.push({ slug: key, name: String(name) })
    }
    return { options, index }
  }

  // Product Type source
  const ptSource = useMemo(() => buildSource(
    productTypeSelection,
    (it) => it?.name ?? it?.product_type?.name ?? it?.label ?? '',
    (it, name) => it?.slug ?? it?.product_type?.slug ?? it?.code ?? it?.key ?? toSlug(name)
  ), [productTypeSelection])

  const ptOptions = ptSource.options
  const ptIndex = ptSource.index
  const ptSelectedSet = useMemo(() => new Set(selectedProductTypeSlugs.map(String)), [selectedProductTypeSlugs])

  const ptFiltered = useMemo(() => {
    const k = ptKeyword.trim().toLowerCase()
    if (!k) return ptOptions
    return ptOptions.filter((d) =>
      d.name.toLowerCase().includes(k) || d.slug.toLowerCase().includes(k)
    )
  }, [ptKeyword, ptOptions])

  const togglePt = (slug, isDisabled) => {
    if (isDisabled) return // cegah toggle kalau sudah ada
    const key = String(slug)
    const s = new Set(ptSelectedSet)
    s.has(key) ? s.delete(key) : s.add(key)
    setSelectedProductTypeSlugs(Array.from(s))
  }

  const selectAllFiltered = () => {
    const s = new Set(ptSelectedSet)
    ptFiltered
      .filter(d => !disabledPtSet.has(String(d.slug))) // hanya yang belum pernah dipilih
      .forEach((d) => s.add(String(d.slug)))
    setSelectedProductTypeSlugs(Array.from(s))
  }

  const clearAllPt = () => setSelectedProductTypeSlugs([])

  const selectedProductTypeRows = useMemo(
    () => selectedProductTypeSlugs.map((slug) => ptIndex.get(String(slug))).filter(Boolean),
    [selectedProductTypeSlugs, ptIndex]
  )

  // Variant source
  const vsSource = useMemo(() => buildSource(
    variantSelection,
    (it) => it?.name ?? it?.variants?.name ?? it?.label ?? '',
    (it, name) => it?.slug ?? it?.variants?.slug ?? it?.code ?? it?.key ?? toSlug(name)
  ), [variantSelection])

  const vsOptions = vsSource.options
  const vsIndex = vsSource.index
  const vsSelectedSet = useMemo(() => new Set(selectedVariantSlugs.map(String)), [selectedVariantSlugs])

  const vsFiltered = useMemo(() => {
    const k = vsKeyword.trim().toLowerCase()
    if (!k) return vsOptions
    return vsOptions.filter((d) =>
      d.name.toLowerCase().includes(k) || d.slug.toLowerCase().includes(k)
    )
  }, [vsKeyword, vsOptions])

  const toggleVs = (slug, isDisabled) => {
    if (isDisabled) return
    const key = String(slug)
    const s = new Set(vsSelectedSet)
    s.has(key) ? s.delete(key) : s.add(key)
    setSelectedVariantSlugs(Array.from(s))
  }

  const selectAllFilteredVs = () => {
    const s = new Set(vsSelectedSet)
    vsFiltered
      .filter(d => !disabledVsSet.has(String(d.slug)))
      .forEach((d) => s.add(String(d.slug)))
    setSelectedVariantSlugs(Array.from(s))
  }

  const clearAllVs = () => setSelectedVariantSlugs([])

  const selectedVariantRows = useMemo(
    () => selectedVariantSlugs.map((slug) => vsIndex.get(String(slug))).filter(Boolean),
    [selectedVariantSlugs, vsIndex]
  )

  /* ======================================================= */

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!nProgress.isStarted()) {
      nProgress.start()

      onSubmit({
        product_type_rows: selectedProductTypeRows,
        variant_rows: selectedVariantRows,
        product_type_slugs: selectedProductTypeSlugs,
        variant_slugs: selectedVariantSlugs
      })

      nProgress.done()
    } else {
      toast.error("Previous action is loading")
    }
  }

  return (
    <div className="mt-8 space-y-8">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">

          {/* Multiselect Product Type */}
          <div>
            <Label htmlFor="product_types" value="Category" />
            <div className="relative inline-block" ref={ptRef}>
              <button
                type="button"
                onClick={() => setPtOpen(v => !v)}
                className="inline-flex items-center gap-2 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm font-medium hover:bg-neutral-50"
              >
                Category{selectedProductTypeSlugs.length ? ` (${selectedProductTypeSlugs.length})` : ''}
                <svg width="14" height="14" viewBox="0 0 20 20" className="opacity-70">
                  <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>

              {ptOpen && (
                <div className="absolute z-50 mt-2 w-80 rounded-xl border border-neutral-200 bg-white shadow-xl">
                  <div className="p-3 border-b border-neutral-100">
                    <input
                      value={ptKeyword}
                      onChange={(e) => setPtKeyword(e.target.value)}
                      placeholder="Cari kategori…"
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={selectAllFiltered}
                        className="rounded-md bg-neutral-100 px-2 py-1 text-xs hover:bg-neutral-200"
                      >
                        Pilih semua (terfilter)
                      </button>
                      <button
                        type="button"
                        onClick={clearAllPt}
                        className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-600 hover:bg-red-100"
                      >
                        Hapus semua
                      </button>
                      <div className="ml-auto" />
                      <button
                        type="button"
                        onClick={() => setPtOpen(false)}
                        className="rounded-md px-2 py-1 text-xs hover:bg-neutral-50"
                      >
                        Tutup
                      </button>
                    </div>
                  </div>

                  <div className="max-h-64 overflow-auto p-2">
                    {isLoadingProductType && (
                      <div className="p-3 text-center text-sm text-neutral-500">Memuat…</div>
                    )}
                    {!isLoadingProductType && (ptFiltered?.length ? ptFiltered : []).map((item) => {
                      const isDisabled = disabledPtSet.has(String(item.slug))
                      const checked = ptSelectedSet.has(String(item.slug))
                      return (
                        <label
                          key={String(item.slug)}
                          className={`flex items-center gap-2 rounded-lg px-2 py-2 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'}`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            disabled={isDisabled}
                            checked={checked}
                            onChange={() => togglePt(item.slug, isDisabled)}
                          />
                          <span className="text-sm">{item.name}</span>
                          {isDisabled && (
                            <span className="ml-2 rounded bg-neutral-100 px-2 py-[2px] text-[10px] text-neutral-500">
                              Sudah ada
                            </span>
                          )}
                          <span className="ml-auto text-[10px] text-neutral-400">/{item.slug}</span>
                        </label>
                      )
                    })}
                    {!isLoadingProductType && !(ptFiltered?.length) && (
                      <div className="p-3 text-center text-sm text-neutral-500">Tidak ada data</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <ErrorMessage error={errors.product_type_rows} />
          </div>

          {/* Multiselect Variant */}
          <div>
            <Label htmlFor="variants" value="Variant(s)" />
            <div className="relative inline-block" ref={vsRef}>
              <button
                type="button"
                onClick={() => setVsOpen(v => !v)}
                className="inline-flex items-center gap-2 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm font-medium hover:bg-neutral-50"
              >
                Variant{selectedVariantSlugs.length ? ` (${selectedVariantSlugs.length})` : ''}
                <svg width="14" height="14" viewBox="0 0 20 20" className="opacity-70">
                  <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>

              {vsOpen && (
                <div className="absolute z-50 mt-2 w-80 rounded-xl border border-neutral-200 bg-white shadow-xl">
                  <div className="p-3 border-b border-neutral-100">
                    <input
                      value={vsKeyword}
                      onChange={(e) => setVsKeyword(e.target.value)}
                      placeholder="Cari variant…"
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={selectAllFilteredVs}
                        className="rounded-md bg-neutral-100 px-2 py-1 text-xs hover:bg-neutral-200"
                      >
                        Pilih semua (terfilter)
                      </button>
                      <button
                        type="button"
                        onClick={clearAllVs}
                        className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-600 hover:bg-red-100"
                      >
                        Hapus semua
                      </button>
                      <div className="ml-auto" />
                      <button
                        type="button"
                        onClick={() => setVsOpen(false)}
                        className="rounded-md px-2 py-1 text-xs hover:bg-neutral-50"
                      >
                        Tutup
                      </button>
                    </div>
                  </div>

                  <div className="max-h-64 overflow-auto p-2">
                    {isLoadingVariant && (
                      <div className="p-3 text-center text-sm text-neutral-500">Memuat…</div>
                    )}
                    {!isLoadingVariant && (vsFiltered?.length ? vsFiltered : []).map((item) => {
                      const isDisabled = disabledVsSet.has(String(item.slug))
                      const checked = vsSelectedSet.has(String(item.slug))
                      return (
                        <label
                          key={String(item.slug)}
                          className={`flex items-center gap-2 rounded-lg px-2 py-2 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'}`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            disabled={isDisabled}
                            checked={checked}
                            onChange={() => toggleVs(item.slug, isDisabled)}
                          />
                          <span className="text-sm">{item.name}</span>
                          {isDisabled && (
                            <span className="ml-2 rounded bg-neutral-100 px-2 py-[2px] text-[10px] text-neutral-500">
                              Sudah ada
                            </span>
                          )}
                          <span className="ml-auto text-[10px] text-neutral-400">/{item.slug}</span>
                        </label>
                      )
                    })}
                    {!isLoadingVariant && !(vsFiltered?.length) && (
                      <div className="p-3 text-center text-sm text-neutral-500">Tidak ada data</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <ErrorMessage error={errors.variant_rows} />
          </div>

        </div>
      </div>

      {/* Submit */}
      <div className="text-xs">
        <button onClick={handleSubmit} className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
          <span>{lang.save}</span>
        </button>
      </div>
    </div>
  )
}

const Add = ({ tags, onSubmit }) => {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = () => setIsOpen(true)
  const closeModal = () => setIsOpen(false)

  const handleSubmit = (data) => {
    onSubmit(data)
    closeModal()
  }

  return (
    <>
      <button type="button" onClick={openModal} className="inline-flex items-center px-4 py-3 space-x-2 transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90">
        <Plus className="w-4 h-4" stroke={1.5} />
        <span>{lang.create}</span>
      </button>
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title={`${lang.create} ${lang.data}`}
        content={<Content tags={tags} onSubmit={handleSubmit} />}
      />
    </>
  )
}

export default Add
